import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { extrairIds } from "../../../../utils/Arrays";
import { 
  findAcaoQualificacao, 
  createAcaoQualificacao, 
  updateAcaoQualificacao 
} from "../../../../API/apiAcoes";
import { AcaoQualificacaoInsert } from "../../../../types/acao";

const initialAcaoState: AcaoQualificacaoInsert = {
  tipologia_id: 0,
  percurso_formativo: "",
  modalidade_id: 0,
  nome: "",
  data_inicio: "",
  data_fim: "",
  observacoes: "",
  arquivo: "",
  temas: [],
};

export const useAcaoSave = (acaoId?: string | null) => {

  const navigate = useNavigate();

  const [msgError, setMsgError ] = useState<string | null>(null);
  const [msgSuccess, setMsgSuccess ] = useState<string | null>(null);
  const [loading, setLoading] = useState< boolean>(false);
  const [loadingSave, setLoadingSave] = useState< boolean>(false);
  const [isEdit, setIsEdit] = useState< boolean>(false);
  const [acao, setAcao]= useState<AcaoQualificacaoInsert>(initialAcaoState);
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [updateArquivo, setUpdateArquivo] = useState<boolean>(false);

  useEffect(() => {
    if (acaoId) {
      consultarAcao();
      setIsEdit(true);
      return;
    }

    setIsEdit(false);
    setAcao(initialAcaoState);
    setMsgError(null);
    setMsgSuccess(null);
    resetArquivos();
  }, [acaoId]);

  useEffect(() => {
    if (msgSuccess) {
      setTimeout(() => {
        setMsgSuccess(null);
      }, 5000);
    }
  }, [msgSuccess]);

  useEffect(() => {
    if (msgError) {
      setTimeout(() => {
        setMsgError(null);
      }, 5000);
    }
  }, [msgError]);

  useEffect(() => {
    if (!arquivo) {
      setPreview(null);
      return;
    }
    const objectUrl = URL.createObjectURL(arquivo);
    setPreview(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [arquivo]);

  const resetArquivos = () => {
    setArquivo(null);
    setPreview(null);
    setUpdateArquivo(false);
  };

  const cancelUpdateArquivo = () => {
    setUpdateArquivo(false);
    setArquivo(null);
  };

  const consultarAcao = async () => {

    if (!acaoId) return;

    setLoading(true);

    try {

      const result = await findAcaoQualificacao(acaoId);

      if (!result) {
        setMsgError('Não foi possível encontrar a ação');
        navigate(`/acoes-qualificacao`);
      }

      setAcao({
        tipologia_id: result.tipologia_id,
        percurso_formativo: result.percurso_formativo || "",
        modalidade_id: result.modalidade_id,
        nome: result.nome,
        data_inicio: result.data_inicio,
        data_fim: result.data_fim,
        observacoes: result.observacoes,
        arquivo: result.arquivo_path,
        temas: result.temas.length > 0 ? extrairIds(result.temas, 'tema_id') : [],
      });

      if (!result.arquivo_path || result.arquivo_path == "") {
        setUpdateArquivo(true);
      }

      setIsEdit(true);

      setLoading(false);

    } catch (error) {
      setLoading(false);

      setMsgError('Ação não encontrada');
    }
  };

  const handleInputChange = (value: string | number, nameObject: string, isNumber?: boolean) => {
    setAcao({ ...acao, [nameObject]: isNumber ? Number(value) : value });
  };

  const handleChange = (field: keyof AcaoQualificacaoInsert, value: any) => {
    setAcao({ ...acao, [field]: value });
  };

  const handleTemasChange = (value: (string | number)[]) => {
    const numericIds = value.map(id => Number(id));
    setAcao({ ...acao, temas: numericIds });
  };

  const validateForm = () : boolean => {
    if (!acao.nome) {
      setMsgError('Informe o nome da ação');
      return false;
    }

    if (!acao.tipologia_id) {
      setMsgError('Selecione a tipologia');
      return false;
    }

    if (!acao.percurso_formativo) {
      setMsgError('Selecione o percurso formativo');
      return false;
    }

    if (!acao.modalidade_id) {
      setMsgError('Selecione a modalidade');
      return false;
    }

    if (!acao.data_inicio) {
      setMsgError('Informe a data de início');
      return false;
    }

    if (!acao.data_fim) {
      setMsgError('Informe a data de término');
      return false;
    }

    if (acao.temas.length === 0) {
      setMsgError('Selecione pelo menos um tema');
      return false;
    }

    return true;
  };

  const saveAcao = async () => {

    if (!validateForm()) {
      return;
    }

    setLoadingSave(true);

    try {

      const formData = new FormData();
      formData.append('nome', acao.nome);
      formData.append('tipologia_id', String(acao.tipologia_id));
      formData.append('percurso_formativo', acao.percurso_formativo);
      formData.append('modalidade_id', String(acao.modalidade_id));
      formData.append('data_inicio', acao.data_inicio);
      formData.append('data_fim', acao.data_fim);
      formData.append('observacoes', acao.observacoes);
      acao.temas.forEach(tema => {
        formData.append('temas[]', String(tema));
      });
      if (arquivo) {
        formData.append('arquivo', arquivo);
      }

      if (acaoId) {

        const update = await updateAcaoQualificacao(acaoId, formData);

        if (!update.id) {
          setMsgError('Não foi possível atualizar a ação de qualificação');
          return false;
        }

        setMsgSuccess('Ação de qualificação atualizada com sucesso!');
        setLoadingSave(false);
        resetArquivos();
        consultarAcao();
        return;

      } else {

        const insert = await createAcaoQualificacao(formData);
        
        if (!insert.id) {
          setMsgError('Não foi possível cadastrar a ação de qualificação');
          return false;
        }
        
        setMsgSuccess('Ação de qualificação cadastrada com sucesso!');
        setLoadingSave(false);
        navigate(`/gestao-turmas?id=${insert.id}`);
      }
      
    } catch (error) {
      setLoadingSave(false);
      setMsgError('Não foi possível cadastrar a ação de qualificação');
      return false;
    }
  };

  return {
    msgError,
    msgSuccess,
    loading,
    loadingSave,
    acao,
    isEdit,
    setAcao,
    handleInputChange,
    handleChange,
    handleTemasChange,
    saveAcao,
    arquivo,
    setArquivo,
    preview,
    updateArquivo,
    setUpdateArquivo,
    cancelUpdateArquivo,
  };
};
